import React from 'react';
import { PageBanner } from '../components/PageBanner';
import { ContactCTA } from '../components/ContactCTA';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { ActivityIcon, ShieldCheckIcon, HeartPulseIcon, TimerIcon, CheckCircleIcon, BandageIcon } from 'lucide-react';
export function WoundCarePage() {
  const {
    ref: introRef,
    isVisible: isIntroVisible
  } = useScrollAnimation();
  const {
    ref: benefitsRef,
    isVisible: isBenefitsVisible
  } = useScrollAnimation();
  const {
    ref: detailsRef,
    isVisible: isDetailsVisible
  } = useScrollAnimation();
  const {
    ref: ctaRef,
    isVisible: isCtaVisible
  } = useScrollAnimation();
  return <main data-id="element-1345">
      <PageBanner title="Wound Care & Management" subtitle="Expert treatment for complex, chronic, and non-healing wounds." breadcrumbs={[{
      label: 'Services',
      path: '/services'
    }, {
      label: 'Wound Care',
      path: '/services/wound-care'
    }]} data-id="element-1346" />

      {/* Intro Section */}
      <section className="py-16 md:py-20 bg-light-alt shadow-md" data-id="element-1347">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1348">
          <div ref={introRef} className={`transition-all duration-1000 ease-out ${isIntroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} data-id="element-1349">
            <div className="inline-flex items-center space-x-2 bg-primary/10 rounded-full px-4 py-2 mb-6" data-id="element-1350">
              <BandageIcon className="h-4 w-4 text-primary" data-id="element-1351" />
              <span className="text-primary text-sm font-semibold" data-id="element-1352">
                Specialized Care
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-dark mb-6 leading-tight" data-id="element-1353">
              Advanced Therapies for Faster Healing
            </h2>
            <div className="space-y-5 text-dark-muted text-lg leading-relaxed" data-id="element-1354">
              <p data-id="element-1355">
                We treat complex, chronic, and non-healing wounds. Our nurses
                utilize advanced therapies to promote faster healing, prevent
                infection, and manage pain for conditions like surgical sites,
                pressure ulcers, and diabetic wounds.
              </p>
            </div>

            {/* RN Badge */}
            <div className="mt-8 flex items-center bg-primary/5 border border-primary/10 rounded-xl p-5" data-id="element-1356">
              <ShieldCheckIcon className="h-10 w-10 text-primary mr-4 flex-shrink-0" data-id="element-1357" />
              <div data-id="element-1358">
                <h4 className="font-bold text-dark" data-id="element-1359">RN Supervised Care</h4>
                <p className="text-sm text-dark-muted" data-id="element-1360">
                  All wound care treatments are administered and supervised by
                  highly trained Registered Nurses.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 md:py-20 bg-light shadow-inner" data-id="element-1361">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1362">
          <div ref={benefitsRef} className={`transition-all duration-1000 ease-out ${isBenefitsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} data-id="element-1363">
            <div className="text-center mb-14" data-id="element-1364">
              <h2 className="text-3xl md:text-4xl font-bold text-dark mb-4" data-id="element-1365">
                Comprehensive Wound Management
              </h2>
              <div className="w-16 h-1.5 bg-secondary mx-auto mb-6 rounded-full" data-id="element-1366"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1367">
              {[{
              icon: ActivityIcon,
              title: 'Advanced Therapies',
              desc: 'Utilizing the latest evidence-based treatments and dressings for optimal recovery.'
            }, {
              icon: ShieldCheckIcon,
              title: 'Infection Prevention',
              desc: 'Strict protocols and monitoring to prevent complications and ensure safety.'
            }, {
              icon: HeartPulseIcon,
              title: 'Pain Management',
              desc: 'Focused on minimizing discomfort during dressing changes and healing.'
            }, {
              icon: TimerIcon,
              title: 'Faster Healing',
              desc: "Targeted interventions designed to accelerate the body's natural healing process."
            }].map((benefit, idx) => <div key={idx} className="bg-light-alt rounded-2xl p-7 shadow-card hover:shadow-card-hover border border-gray-200 transition-all duration-300 transform hover:-translate-y-1 group" data-id="element-1368">
                  <div className="bg-primary/10 w-14 h-14 rounded-xl flex items-center justify-center mb-5 group-hover:bg-primary transition-colors duration-300" data-id="element-1369">
                    <benefit.icon className="h-7 w-7 text-primary group-hover:text-white transition-colors duration-300" data-id="element-1370" />
                  </div>
                  <h3 className="text-lg font-bold text-dark mb-3" data-id="element-1371">
                    {benefit.title}
                  </h3>
                  <p className="text-dark-muted text-sm leading-relaxed" data-id="element-1372">
                    {benefit.desc}
                  </p>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Details Section */}
      <section className="py-16 md:py-20 bg-light-alt shadow-inner" data-id="element-1373">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1374">
          <div ref={detailsRef} className={`transition-all duration-1000 ease-out ${isDetailsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} data-id="element-1375">
            <h2 className="text-3xl font-bold text-dark mb-6" data-id="element-1376">
              Our Wound Care Services Include
            </h2>
            <p className="text-dark-muted text-lg leading-relaxed mb-8" data-id="element-1377">
              Every wound is unique. Our team develops a customized care plan in
              coordination with your physician to address your specific needs
              and promote the best possible outcomes.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1378">
              {['Surgical site wound care', 'Pressure ulcer treatment', 'Diabetic wound management', 'Advanced wound dressings', 'Infection monitoring & prevention', 'RN-supervised wound assessment'].map((item, idx) => <div key={idx} className="flex items-center p-4 bg-light rounded-xl border border-gray-200" data-id="element-1379">
                  <CheckCircleIcon className="h-6 w-6 text-secondary mr-3 flex-shrink-0" data-id="element-1380" />
                  <span className="text-dark font-medium" data-id="element-1381">{item}</span>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16 bg-light" data-id="element-1382">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1383">
          <div ref={ctaRef} className={`bg-primary rounded-3xl p-10 md:p-14 text-center shadow-xl relative overflow-hidden transition-all duration-1000 ease-out ${isCtaVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-12 scale-95'}`} data-id="element-1384">
            <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: 'radial-gradient(#ffffff 2px, transparent 2px)',
            backgroundSize: '30px 30px'
          }} data-id="element-1385"></div>
            <div className="relative z-10" data-id="element-1386">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{
              fontFamily: "'Cinzel', serif"
            }} data-id="element-1387">
                Need Expert Wound Care?
              </h2>
              <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto" data-id="element-1388">
                Contact us today to discuss your care needs and schedule a
                consultation with our nursing team.
              </p>
              <a href="#/contact" className="inline-block bg-white text-primary-dark hover:bg-light-alt px-10 py-4 rounded-xl font-bold text-lg transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1" data-id="element-1389">
                Contact Us Today
              </a>
            </div>
          </div>
        </div>
      </section>

      <ContactCTA data-id="element-1390" />
    </main>;
}